/**
 * The `field` is the container for all form fields.
 * - It provides the consistent `label` + `input` experience
 * - It manages the state for the `input` and ensures that it stays valid
 */
import * as React from 'react';
import { colors, fontSizes } from '../base/styles';
import { Label } from '../base/label';
import { debounce } from '../utils/utils';
import * as validation from './validation/validation';
import * as typestyle from 'typestyle';
import * as csstips from 'csstips';
import { action, observable, computed, makeObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

/**
 * The Field Styles
 */
export namespace FieldStyles {
  export const rootClassName = typestyle.style(
    {
      /** This is fix for IE */
      flexShrink: 0,
      flexBasis: 'auto'
    },
    csstips.flex,
    csstips.flexRoot,
    csstips.vertical
  );

  export const inputContainerClassName = typestyle.style(csstips.flexRoot)

  export const errorTextClassName = typestyle.style({ fontSize: fontSizes.smallText, color: colors.red })
}

/**
 * All properties of Field except `input`
 */
export interface FieldPropsWithoutInput<TValue> {
  className?: string,
  /** Optional. You can use a field without a label in which case we hide the label */
  label?: React.ReactNode,
  /** Optional. Adds a `*` next to the label. Only works if you have a label */
  star?: boolean,

  /** Holds key value + validation information */
  fieldState: FieldState<TValue>,

  /** Optional. Allows you to listen to changes */
  onChange?: (val: TValue) => any;
  onKeyDown?: (e: React.SyntheticEvent<any>) => void;
  onKeyUp?: (e: React.SyntheticEvent<any>) => void;
  onKeyPress?: (e: React.SyntheticEvent<any>) => void;

  /** Passes down to the input element */
  id?: string,
  name?: string,
  readOnly?: boolean,
  takeFocus?: boolean,
  onFocus?: () => void,
  onBlur?: () => void,
}

/**
 * The properties for the users of the Field component
 * The `input` is the key differentiating factor between inputs
 */
export interface FieldProps<TValue> extends FieldPropsWithoutInput<TValue> {
  /**
   * The input element constructor
   */
  input: (props: InputProps<TValue>) => React.ReactNode;
}

export const FieldErrorText = ({ text, id }: { text: string, id?: string }) => {
  return <div id={id} className={FieldStyles.errorTextClassName} aria-live="assertive">
    {text}
  </div>;
}

/**
 * This is the container for input controls
 * Provides unified functionality of layout (e.g label) + error handling
 * You would compose from this to create a specialized version that wraps your component
 */
export class Field<TValue> extends React.Component<FieldProps<TValue>, {}>{
  render() {  
    const { fieldState } = this.props;
    const error = fieldState.error;
    const inputProps = getPropsForInput(this.props);

    return (
      <div data-comment="Field" className={typestyle.classes(FieldStyles.rootClassName, this.props.className)}>
        {this.props.label && <div style={{ marginBottom: '8px' }}>
          <Label htmlFor={this.props.id} content={this.props.label} error={this.props.fieldState.error} />
          {this.props.star && <span style={{ color: colors.red, marginLeft: '2px' }}>*</span>}
        </div>}
        <div className={FieldStyles.inputContainerClassName}>
          {this.props.input(inputProps)}
        </div>
        {
          this.props.fieldState.error
          && <FieldErrorText id={this.props.id ? this.props.id + 'Error' : ''} text={this.props.fieldState.error} />
        }
      </div>
    );
  }
}

// Export an observer-wrapped Field to avoid relying on decorator transforms
export const ObservedField = observer(Field as any) as typeof Field;

// Keep default export as the observer-wrapped Field for existing imports
export default ObservedField;

/**
 * Utility: clones props for use with inputs
 * Redirects key functions to `Field` so its validation can kick in
 */
export function getPropsForInput<TValue>(props: FieldPropsWithoutInput<TValue>): InputProps<TValue> {
  let inputProps: InputProps<TValue> = {
    /** Pipe through */
    id: props.id,
    name: props.name,
    readOnly: (!!props.readOnly),
    takeFocus: (!!props.takeFocus),

    /** Get from fieldState */
    value: props.fieldState.value,
    error: props.fieldState.error,
    onChange: (val) => {
      props.onChange && props.onChange(val);
      props.fieldState.onChange(val);
    },
    onKeyDown: props.onKeyDown,
    onKeyUp: props.onKeyUp,
    onKeyPress: props.onKeyPress

};
  return inputProps;
}


/**
 * The base props for all inputs
 * The input component accepts these from the Field component
 */
export interface InputProps<TValue> {
  id?: string;
  name?: string;
  value: TValue;
  onChange: (value: TValue) => void;
  onKeyDown: (e: React.SyntheticEvent<any>) => void;
  onKeyUp: (e: React.SyntheticEvent<any>) => void;
  onKeyPress: (e: React.SyntheticEvent<any>) => void;
  readOnly?: boolean;
  takeFocus?: boolean;
  error?: string;
}

/** Anything that provides this interface can be composed into the validation system */
interface Validatable {
  validate(): { hasError: boolean };
  hasError: boolean;
}

/**
 * Helps maintain the value + error information about a field
 *
 * This is the glue between the *page* and *field* in the presence of invalid states.
 */
export class FieldState<TValue> implements Validatable {
  /**
   * The value is stored in the field. May or may not be *valid*.
   */
  value: TValue;

  /** If there is any error on the field on last validation attempt */
  error?: string;

  constructor(public config: {
    value: TValue,
    onUpdate?: (state: FieldState<TValue>) => any,
    validators?: validation.Validator<TValue>[],
  }) {
    this.value = config.value;
  this.error = undefined;
  makeObservable(this, {
      value: observable,
      error: observable,
      onChange: action,
      setValue: action,
      validate: action,
      onUpdate: action,
    });
  }

  /** On change on the component side */
  onChange(value: TValue) {
    // Immediately set for local ui binding
    this.value = value;
    this.onUpdate();
    this.refreshError();
  }

  /** On change on the page side */
  setValue(value: TValue) {
    // This value vetos all previous values
    this.value = value;
    this.error = undefined;
    this.onUpdate();
  }
 
  get hasError() {
    return !!this.error;
  }

  /**
   * Runs validation on the current value immediately
   */
  private lastValidationRequest: number = 0;
  validate() {
    this.lastValidationRequest++;
    const lastValidationRequest = this.lastValidationRequest;
    const fieldError = validation.applyValidators(this.value, this.config.validators || []);
    if (this.lastValidationRequest !== lastValidationRequest) return { hasError: this.hasError };
    if (this.error !== fieldError) {
      this.error = fieldError;
    }
    this.onUpdate();
    return { hasError: this.hasError };
  }

  /**
   * Runs validation with debouncing to keep the UI super smoothly responsive
   */
 
  refreshError() {
    return this.validate();
  }

  onUpdate() {
    this.config.onUpdate && this.config.onUpdate(this);
  }
}

/**
 * Just a wrapper around the helpers for a set of FieldStates or FormStates
 */
export class FormState implements Validatable {
  private getSubItems: () => Validatable[];
  validating: boolean;
  constructor(
    /**
     * It is a function: To allow you to dynamically and / remove fields as needed in your form
     * SubItems can be `FieldState` or another `FormState`
     */
    getSubItems: () => Validatable[]
  ) {
  this.getSubItems = getSubItems;
  this.validating = false;
  makeObservable(this, {
      validating: observable,
      validate: action,
      hasError: computed,
    });
  }
  

  /**
   * - Re-runs validation on all fields
   * - returns `hasError`
   */
  validate() {
  this.validating = true;
  this.getSubItems().forEach(f => f.validate());
  this.validating = false;
  return { hasError: this.hasError };
}
  /**
   * Does any field have an error
   */
  get hasError(): boolean {
    return this.getSubItems().some(f => f.hasError);
  }
}